import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Select, Button, Icon, Checkbox, message, WkdUploadCore } from '@common/musicHubCommon';
import type { UploadFileInfo } from '@common/musicHubCommon';
import type { ChangeEvent } from 'react';

const { TextArea } = Input;
import { ROUTES } from '../../../routes';
import { editMusic } from '../../../services/index';
import { getArtistID, useGenreStore, useMoodStore } from '../../../store';
import './index.scss';

interface Song {
  id: string;           // musicID
  artistID?: string;
  title: string;
  album: string;
  cover: string;
  genres: string[];
  releaseDate: string;
  publishTime?: number;
  status: 'Under Review' | 'Released' | 'Rejected';
  artists?: string[];
  producer?: string;
  moods?: string[];
  label?: string;
  publisher?: string;
  isrc?: string;
  explicit?: string;
  pLine?: string;
  note?: string;
}

interface EditFormData {
  songTitle: string;
  coverArtPreview: string | null;
  featuredArtists: string[];  // Featured Artists（非必填）
  producer: string;
  genres: string[];
  moods: string[];
  // Advanced fields
  recordLabel: string;
  publisher: string;
  isrc: string;
  explicitContent: boolean;
  pLine: string;
  fromTheArtist: string;
}

const EditSong: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isCoverHovered, setIsCoverHovered] = useState(false);
  
  // 从 store 获取 genre 数据，mood 使用常量（后端暂未提供接口）
  const GENRE_OPTIONS = useGenreStore((state) => state.genreOptions);
  const MOOD_OPTIONS = useMoodStore((state: any) => state.moodOptions);
  // 从 sessionStorage 获取歌曲信息
  const songData = useMemo<Song | null>(() => {
    const stored = sessionStorage.getItem('editSongData');
    return stored ? JSON.parse(stored) : null;
  }, []);

  // 如果没有歌曲数据，跳回列表页
  useEffect(() => {
    if (!songData) {
      navigate(ROUTES.mySongs);
    }
  }, [songData, navigate]);

  // 表单数据
  const [formData, setFormData] = useState<EditFormData>({
    songTitle: songData?.title || '',
    coverArtPreview: songData?.cover || null,
    featuredArtists: songData?.artists?.length ? songData.artists : [],  // Featured Artists 初始为空
    producer: songData?.producer || '',
    genres: songData?.genres?.length ? songData.genres : [''],
    moods: songData?.moods?.length ? songData.moods : [''],
    // Advanced fields
    recordLabel: songData?.label || '',
    publisher: songData?.publisher || '',
    isrc: songData?.isrc || '',
    explicitContent: songData?.explicit === '1',
    pLine: songData?.pLine || '',
    fromTheArtist: songData?.note || '',
  });

  // 处理返回
  const handleBack = () => {
    const returnTo = sessionStorage.getItem('editSongReturnTo');
    // 清理 sessionStorage
    sessionStorage.removeItem('editSongData');
    sessionStorage.removeItem('editSongReturnTo');
    navigate(returnTo || ROUTES.mySongs);
  };

  // 封面上传成功
  const handleCoverUpload = (fileInfo: UploadFileInfo) => {
    if (fileInfo?.url) {
      setFormData(prev => ({ ...prev, coverArtPreview: fileInfo.url }));
    }
  };

  // Featured Artists 操作
  const handleAddFeaturedArtist = () => setFormData(prev => ({ ...prev, featuredArtists: [...prev.featuredArtists, ''] }));
  const handleFeaturedArtistChange = (index: number, value: string) => {
    setFormData(prev => ({ ...prev, featuredArtists: prev.featuredArtists.map((a, i) => (i === index ? value : a)) }));
  };
  const handleRemoveFeaturedArtist = (index: number) => {
    setFormData(prev => ({ ...prev, featuredArtists: prev.featuredArtists.filter((_, i) => i !== index) }));
  };

  // 流派操作
  const handleGenreChange = (index: number, value: string) => {
    setFormData(prev => ({ ...prev, genres: prev.genres.map((g, i) => (i === index ? value : g)) }));
  };
  const handleAddGenre = () => setFormData(prev => ({ ...prev, genres: [...prev.genres, ''] }));
  const handleRemoveGenre = (index: number) => {
    if (formData.genres.length > 1) setFormData(prev => ({ ...prev, genres: prev.genres.filter((_, i) => i !== index) }));
  };

  // 情绪操作
  const handleMoodChange = (index: number, value: string) => {
    setFormData(prev => ({ ...prev, moods: prev.moods.map((m, i) => (i === index ? value : m)) }));
  };
  const handleAddMood = () => setFormData(prev => ({ ...prev, moods: [...prev.moods, ''] }));
  const handleRemoveMood = (index: number) => {
    if (formData.moods.length > 1) setFormData(prev => ({ ...prev, moods: prev.moods.filter((_, i) => i !== index) }));
  };

  // 表单验证（Featured Artists 非必填）
  const isFormValid = () => {
    const validGenres = formData.genres.filter(g => g.trim() !== '');
    return (
      formData.songTitle.trim() !== '' &&
      formData.coverArtPreview !== null &&
      validGenres.length > 0
    );
  };

  // 保存
  const handleSave = async () => {
    if (!isFormValid() || !songData) {
      message.warning('Please fill all required fields');
      return;
    }

    // Featured Artists 从表单获取
    const featuredArtists = formData.featuredArtists.filter(a => a.trim() !== '');

    try {
      await editMusic({
        musicID: songData.id,
        artistID: songData.artistID || getArtistID(),
        title: formData.songTitle,
        cover: formData.coverArtPreview || '',
        featuredArtist: featuredArtists,
        producer: formData.producer,
        genre: formData.genres.filter(g => g).map(g => Number(g)),
        mood: formData.moods.filter(m => m).map(m => Number(m)),
        label: formData.recordLabel,
        publisher: formData.publisher,
        isrc: formData.isrc,
        explicit: formData.explicitContent ? '1' : '0',
        pLine: formData.pLine,
        note: formData.fromTheArtist,
        publishTime: songData.publishTime || Math.floor(Date.now() / 1000),
      });

      message.success('Song saved successfully');
      handleBack();
    } catch (error) {
      console.error('Failed to save song:', error);
      message.error('Failed to save song');
    }
  };

  // 如果没有歌曲数据，不渲染内容
  if (!songData) {
    return null;
  }

  return (
    <div className="edit-song">
      {/* Header */}
      <div className="edit-song__header">
        <button className="back-btn" onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
          <span>Edit Song</span>
        </button>
      </div>

      {/* Content */}
      <div className="edit-song__content">
        {/* Basic Information */}
        <div className="edit-song__card">
          <h2 className="edit-song__title">Basic Information</h2>
          <Form form={form} layout="vertical" className="edit-song__form">
            {/* Song Title */}
            <Form.Item label="Song Title" required className="form-item">
              <Input
                value={formData.songTitle}
                onChange={(e) => setFormData(prev => ({ ...prev, songTitle: e.target.value }))}
                placeholder="Enter song title"
              />
            </Form.Item>

            {/* Cover Art */}
            <Form.Item label="Cover Art" required className="form-item">
              <WkdUploadCore
                accept="image/jpeg,image/jpg,image/png"
                size={10 * 1024}
                // ratio={1}
                onChange={handleCoverUpload}
                onFaild={(error) => message.error(String(error))}
              >
                <div
                  className={`cover-upload ${isCoverHovered ? 'cover-upload--hover' : ''}`}
                  onMouseEnter={() => setIsCoverHovered(true)}
                  onMouseLeave={() => setIsCoverHovered(false)}
                >
                  {formData.coverArtPreview ? (
                    <>
                      <img src={formData.coverArtPreview} alt="Cover" className="cover-upload__preview" />
                      <div className="cover-upload__overlay">
                        <span>Change</span>
                      </div>
                    </>
                  ) : (
                    <div className="cover-upload__placeholder">
                      <svg width="48" height="48" viewBox="0 0 48 48" fill="white">
                        <path d="M38 26H26V38H22V26H10V22H22V10H26V22H38V26Z" />
                      </svg>
                    </div>
                  )}
                </div>
              </WkdUploadCore>
              <p className="form-item__hint">Min 1000x1000 pixels, JPG or PNG. Max 10 MB.</p>
            </Form.Item>

            {/* Featured Artists（非必填） */}
            <Form.Item label="Featured Artists" className="form-item">
              {formData.featuredArtists.map((artist, index) => (
                <div key={index} className="form-item__row">
                  <Input value={artist} onChange={(e) => handleFeaturedArtistChange(index, e.target.value)} placeholder="Enter featured artist name" />
                  <button type="button" className="remove-btn" onClick={() => handleRemoveFeaturedArtist(index)}><Icon style="Close" /></button>
                </div>
              ))}
              <button type="button" className="add-btn" onClick={handleAddFeaturedArtist}><span>+</span> Add Featured Artist</button>
            </Form.Item>

            {/* Producer */}
            <Form.Item label="Producer" className="form-item">
              <Input value={formData.producer} onChange={(e) => setFormData(prev => ({ ...prev, producer: e.target.value }))} placeholder="Producer name" />
            </Form.Item>

            {/* Genre */}
            <Form.Item label="Genre" required className="form-item">
              {formData.genres.map((genre, index) => (
                <div key={index} className="form-item__row">
                  <Select value={genre || undefined} onChange={(value) => handleGenreChange(index, value as string)} placeholder="Select genre" options={GENRE_OPTIONS.filter(g => !formData.genres.includes(g.value) || g.value === genre)} style={{ width: '100%' }} />
                  {formData.genres.length > 1 && <button type="button" className="remove-btn" onClick={() => handleRemoveGenre(index)}><Icon style="Close" /></button>}
                </div>
              ))}
              <button type="button" className="add-btn" onClick={handleAddGenre}><span>+</span> Add Genre</button>
            </Form.Item>

            {/* Mood */}
            <Form.Item label="Song Mood" className="form-item">
              {formData.moods.map((mood, index) => (
                <div key={index} className="form-item__row">
                  <Select value={mood || undefined} onChange={(value) => handleMoodChange(index, value as string)} placeholder="Select mood" options={MOOD_OPTIONS.filter(m => !formData.moods.includes(m.value) || m.value === mood)} style={{ width: '100%' }} />
                  {formData.moods.length > 1 && <button type="button" className="remove-btn" onClick={() => handleRemoveMood(index)}><Icon style="Close" /></button>}
                </div>
              ))}
              <button type="button" className="add-btn" onClick={handleAddMood}><span>+</span> Add Mood</button>
            </Form.Item>
          </Form>
        </div>

        {/* Advanced Details */}
        <div className="edit-song__card">
          <h2 className="edit-song__title">Advanced Details</h2>
          <Form form={form} layout="vertical" className="edit-song__form">
            {/* Record Label */}
            <Form.Item label="Record Label" className="form-item">
              <Input value={formData.recordLabel} onChange={(e) => setFormData(prev => ({ ...prev, recordLabel: e.target.value }))} placeholder="Your record label name" />
            </Form.Item>

            {/* Publisher */}
            <Form.Item label="Publisher" className="form-item">
              <Input value={formData.publisher} onChange={(e) => setFormData(prev => ({ ...prev, publisher: e.target.value }))} placeholder="Publisher name" />
            </Form.Item>

            {/* ISRC */}
            <Form.Item label="ISRC" className="form-item">
              <Input value={formData.isrc} onChange={(e) => setFormData(prev => ({ ...prev, isrc: e.target.value }))} placeholder="International Standard Recording Code" />
            </Form.Item>

            {/* Explicit Content */}
            <Form.Item label="Contains explicit content" className="form-item">
              <p className="form-item__desc">Check this if your track contains explicit content.</p>
              <div className="checkbox-row">
                <Checkbox checked={formData.explicitContent} onChange={(e) => setFormData(prev => ({ ...prev, explicitContent: e.target.checked }))}>Explicit content</Checkbox>
                <div className="explicit-badge">E</div>
              </div>
            </Form.Item>

            {/* P Line */}
            <Form.Item label="P Line" className="form-item">
              <Input value={formData.pLine} onChange={(e) => setFormData(prev => ({ ...prev, pLine: e.target.value }))} placeholder="2026 Your Name" />
            </Form.Item>

            {/* From the Artist */}
            <Form.Item 
              label={
                <div className="form-label-with-counter">
                  <span>From the Artist</span>
                  <span className="counter">{formData.fromTheArtist.length}/500</span>
                </div>
              } 
              className="form-item form-item--full"
            >
              {TextArea && (
                <TextArea
                  value={formData.fromTheArtist}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFormData(prev => ({ ...prev, fromTheArtist: e.target.value.slice(0, 500) }))}
                  placeholder="Share the story behind your music..."
                  rows={4}
                />
              )}
            </Form.Item>

            {/* Save Button */}
            <Button type="primary" onClick={handleSave} className="edit-song__submit">Save</Button>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default EditSong;
